import { createMatchState, step, DT, type MatchState, type SimConfig, type ModeInfo, type PlayerInput, type SimEvent } from '@fafi/shared';
import { toViewState, type ViewState } from './viewState';
import { passSoftLock } from './softLock';

/** Provides one PlayerInput per player for a given tick (controlled human + placeholders/bots). */
export type InputProvider = (state: MatchState) => PlayerInput[];

/** D45 Items 2+5: optional lifecycle hooks so the living kickabout can start the match properly and run a
 *  client-side ControlRouter (evaluate/noteTouch) exactly like MatchRunner does. */
export interface LocalSimHooks {
  /** Run once right after createMatchState — e.g. startKickoff to open like a real small-sided match. */
  onInit?: (state: MatchState) => void;
  /** Run after each step with the PRE-step tick — the seam for noteTouch + cadence-driven evaluate.
   *
   *  D138: `events` is new, and it is the one seam the tutorial needed. `LocalSim` DISCARDED
   *  `step().events` entirely, so local play had no banners, no SFX and — the point — nothing for a
   *  lesson objective to observe. Delivered PER STEP: a catch-up burst of three steps calls this three
   *  times with three separate arrays, never one merged one, because a detector that counts "goals this
   *  frame" and a detector that counts "goals this tick" are different detectors. */
  onTick?: (state: MatchState, fromTick: number, events: readonly SimEvent[]) => void;
}

/**
 * M1 local game loop: runs the shared sim at a fixed 30 Hz with a render-time accumulator, keeping the
 * previous and current tick so the renderer can interpolate. Netcode (server-authoritative prediction/
 * interpolation) replaces this in M2; the sim + ViewState path is identical.
 */
export class LocalSim {
  private state: MatchState;
  private prev: MatchState;
  private acc = 0;

  constructor(
    private readonly cfg: SimConfig,
    mode: ModeInfo,
    seed: number,
    private readonly inputProvider: InputProvider,
    private readonly hooks: LocalSimHooks = {},
  ) {
    this.state = createMatchState(mode, seed);
    this.hooks.onInit?.(this.state);
    this.prev = this.state;
  }

  /** Advance the fixed-timestep sim by `dtSeconds` of wall time (capped to avoid spirals). D52: at most
   *  3 catch-up steps per frame — a hiccup (GC pause, tab lag) used to trigger up to 8 full sim+bot
   *  steps in ONE frame, amplifying the very stutter it was catching up from. Excess time is dropped
   *  (a barely-perceptible slow-mo beats a cascade). */
  advance(dtSeconds: number): void {
    this.acc += Math.min(dtSeconds, 0.25);
    const MAX_STEPS = 3;
    let steps = 0;
    while (this.acc >= DT && steps < MAX_STEPS) {
      this.prev = this.state;
      const fromTick = this.state.tick;
      const r = step(this.state, this.inputProvider(this.state), this.cfg);
      this.state = r.state;
      this.hooks.onTick?.(this.state, fromTick, r.events);
      this.acc -= DT;
      steps += 1;
    }
    if (this.acc >= DT) this.acc = this.acc % DT; // drop the un-simulated remainder
  }

  view(controlledId: number, aimYaw = 0): ViewState {
    const v = toViewState(this.prev, this.state, this.acc / DT, controlledId, this.cfg.actions.kick.CHARGE_CAP);
    // D34 receiver highlight (presentation): same soft-lock the sim will apply on release
    const chargeTicks = this.state.players[controlledId]?.charge?.ticks ?? 0;
    v.passTarget = passSoftLock(v, controlledId, aimYaw, chargeTicks, this.cfg);
    return v;
  }

  get current(): MatchState {
    return this.state;
  }
}
